import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import DicaPrimeiraVez from '@/components/feature/DicaPrimeiraVez';
import { formatarDinheiro } from '../utils';
import type { GrupoVagas } from '../types';
import type { AbaPainel } from './types';
import TileInscritos from './TileInscritos';
import { TilePainel } from '@/pages/gerenciar/components/TilePainel';
import MandarAviso from './MandarAviso';
import AtalhosInicio from './AtalhosInicio';
import { nomeDoGrupo } from '../vagas';
import ListaEntrada from '@/components/movimento/ListaEntrada';
import PulsoDuas from '@/components/movimento/PulsoDuas';
import { useResumoAgente } from '@/pages/gerenciar/assistente/useResumoAgente';

interface Props {
  eventoId: number;
  slug: string;
  vendeIngressos: boolean;
  rotuloPessoas: string;
  mostrarEquipes: boolean;
  mostrarLoja: boolean;
  /** Acampamento: atalhos vão para Equipe ou Campistas. */
  separaEquipeCampista?: boolean;
  /** Entrada franca (Quermesse): vende fichas sem ingresso. */
  entradaFranca?: boolean;
  status: string;
  aoIrPara: (aba: AbaPainel, extra?: Record<string, string>) => void;
  aoPublicar: () => void;
}

interface FinanceiroLinha {
  inscricoes: number;
  recebido_centavos: number;
  a_receber_centavos: number;
  com_pendencia: number;
}

interface ResumoLinha {
  bloco: string;
  item: string;
  quantidade: number | null;
  detalhe: string | null;
}

interface FunilLinha {
  periodo_dias: number;
  pessoas_que_viram: number;
  aberturas_totais: number;
  pessoas_hoje: number;
  fichas_comecadas: number;
  fichas_confirmadas: number;
  fichas_pagas: number;
  de_cada_100_que_veem_quantos_se_inscrevem: number | null;
  recado: string;
}

// funil_do_evento_v2: uma linha por público (nunca somar os públicos).
interface FunilPublico {
  publico: string;
  papel: string | null;
  pessoas_que_viram: number | null;
  fichas_comecadas: number | null;
  fichas_confirmadas: number | null;
  fichas_pagas: number | null;
  suplentes: number | null;
  recado: string | null;
}

interface VagasResumo {
  comVaga: number;
  suplentes: number;
}

interface VagasPorPapel {
  equipe: VagasResumo;
  campista: VagasResumo;
  total: VagasResumo;
}

interface Pendencia {
  chave: string;
  rotulo: string;
  alvo: AbaPainel;
  extra?: Record<string, string>;
  acao?: 'publicar';
  tom: 'aviso' | 'urgente';
  /** Linha de apoio (ex.: o que falta para publicar). */
  detalhe?: string;
  /** Texto do convite, quando existe uma ação clara. */
  rotuloAcao?: string;
}

interface EventoRascunho {
  imagem_desktop_url: string | null;
  inicio_em: string | null;
  local_nome: string | null;
  cidade: string | null;
}

// "arte do evento, local e data" — vírgulas com "e" no fim.
function listaDeFaltas(itens: string[]): string {
  if (itens.length === 0) return '';
  if (itens.length === 1) return itens[0];
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

// O resumo_evento prefixa os blocos com "EQUIPE · " / "CAMPISTAS · ".
function semPrefixo(bloco: string): string {
  return bloco.replace(/^(EQUIPE|CAMPISTAS)\s·\s/, '');
}

function tituloBloco(bloco: string): string {
  return semPrefixo(bloco).replace(/^\d+\s·\s/, '');
}

interface BlocoResumo {
  bloco: string;
  titulo: string;
  linhas: ResumoLinha[];
}

interface GrupoResumo {
  chave: string;
  titulo: string;
  subtitulo: string | null;
  blocos: BlocoResumo[];
}

function agruparBlocos(linhas: ResumoLinha[]): BlocoResumo[] {
  const ordem: string[] = [];
  const mapa = new Map<string, ResumoLinha[]>();
  for (const l of linhas) {
    if (!mapa.has(l.bloco)) {
      mapa.set(l.bloco, []);
      ordem.push(l.bloco);
    }
    mapa.get(l.bloco)!.push(l);
  }
  return ordem.map((b) => ({ bloco: b, titulo: tituloBloco(b), linhas: mapa.get(b)! }));
}

const CARD =
  'rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]';

export default function AbaInicio({
  eventoId,
  slug,
  vendeIngressos,
  rotuloPessoas,
  mostrarEquipes,
  mostrarLoja,
  separaEquipeCampista = false,
  entradaFranca = false,
  status,
  aoIrPara,
  aoPublicar,
}: Props) {
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [financeiro, setFinanceiro] = useState<FinanceiroLinha | null>(null);
  const [portaria, setPortaria] = useState<{ ingressos_validos: number } | null>(null);
  const [semEquipe, setSemEquipe] = useState(0);
  const [resumo, setResumo] = useState<ResumoLinha[]>([]);
  const [vagasResumo, setVagasResumo] = useState<VagasPorPapel | null>(null);
  // Grupos com vaga livre e suplentes esperando (vira "1 vaga livre em Tupã M").
  const [alertasVagas, setAlertasVagas] = useState<{ livres: number; nomes: string[] }>({
    livres: 0,
    nomes: [],
  });
  const [funil, setFunil] = useState<FunilLinha | null>(null);
  const [funilPublicos, setFunilPublicos] = useState<FunilPublico[]>([]);
  const [lojaEstado, setLojaEstado] = useState<string | null>(null);
  // Só cobra "vendas da loja fechadas" quando existe produto ativo para vender.
  const [temProdutoLoja, setTemProdutoLoja] = useState(false);
  const [estornosPendentes, setEstornosPendentes] = useState(0);
  // Pedidos de ficha pagos (loja_painel). null = a loja não respondeu: não inventar.
  const [fichasPagas, setFichasPagas] = useState<number | null>(null);
  // Evento recebe direto na conta do organizador (split do Mercado Pago).
  const [recebeDireto, setRecebeDireto] = useState(false);
  const [premiosSemFoto, setPremiosSemFoto] = useState(0);
  // Líquido (centavos) vindo da mesma rpc do Financeiro, para os dois mostrarem o mesmo número.
  const [liquidoCentavos, setLiquidoCentavos] = useState<number | null>(null);
  const [pendentesPorPapel, setPendentesPorPapel] = useState<{
    equipe: { total: number; pendente: number };
    campista: { total: number; pendente: number };
  } | null>(null);
  const [resumoAberto, setResumoAberto] = useState(false);
  const [eventoRascunho, setEventoRascunho] = useState<EventoRascunho | null>(null);
  const navigate = useNavigate();

  // Mesmo resumo do dia que a tela do agente usa (uma linha só, no cartão).
  const { dados: resumoAgente, estado: estadoAgente } = useResumoAgente(eventoId, true);
  const primeiraLinhaAgente =
    resumoAgente?.resumo
      .split('\n')
      .map((l) => l.trim())
      .find((l) => l.length > 0) ?? '';

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setCarregando(true);
      setErro(null);
      try {
        const resultados = await Promise.allSettled([
          supabase
            .from('vw_financeiro_evento')
            .select('inscricoes, recebido_centavos, a_receber_centavos, com_pendencia')
            .eq('evento_id', eventoId)
            .maybeSingle(),
          supabase
            .from('vw_portaria')
            .select('ingressos_validos')
            .eq('evento_id', eventoId)
            .maybeSingle(),
          supabase.rpc('resumo_evento', { p_evento: eventoId }),
          supabase.rpc('funil_do_evento', { p_evento: eventoId, p_dias: 30 }).maybeSingle(),
          mostrarEquipes
            ? supabase.from('vw_sem_equipe').select('participante_id').eq('evento_id', eventoId)
            : Promise.resolve({ data: null }),
          mostrarLoja ? supabase.rpc('loja_painel', { p_evento: eventoId }) : Promise.resolve({ data: null }),
          mostrarLoja
            ? supabase
                .from('loja_produtos')
                .select('id', { count: 'exact', head: true })
                .eq('evento_id', eventoId)
                .eq('ativo', true)
            : Promise.resolve({ count: 0, error: null }),
          supabase
            .from('vw_rifa_publica')
            .select('status, premios')
            .or(`evento_id.eq.${eventoId},evento_beneficiado_id.eq.${eventoId}`)
            .limit(1)
            .maybeSingle(),
          supabase.rpc('financeiro_do_evento', { p_evento: eventoId }),
          supabase.from('modalidades').select('id, papel').eq('evento_id', eventoId),
          separaEquipeCampista
            ? supabase.from('vw_inscritos').select('modalidade_id, status').eq('evento_id', eventoId)
            : Promise.resolve({ data: null }),
          supabase.rpc('painel_vagas', { p_evento: eventoId }),
          supabase.rpc('funil_do_evento_v2', {
            p_evento: eventoId,
            p_dias: 30,
            p_papel: null,
          }),
          status === 'rascunho'
            ? supabase
                .from('eventos')
                .select('imagem_desktop_url, inicio_em, local_nome, cidade')
                .eq('id', eventoId)
                .maybeSingle()
            : Promise.resolve({ data: null }),
        ]);

        if (!ativo) return;

        const [
          rFin,
          rPort,
          rResumo,
          rFunil,
          rSem,
          rLoja,
          rProd,
          rRifa,
          rFinanc,
          rMods,
          rInsc,
          rVagas,
          rFunilV2,
          rEv,
        ] = resultados;

        if (rFin.status === 'fulfilled' && rFin.value.error) throw rFin.value.error;
        if (rPort.status === 'fulfilled' && rPort.value.error) throw rPort.value.error;
        if (rResumo.status === 'fulfilled' && rResumo.value.error) throw rResumo.value.error;

        if (rFin.status === 'fulfilled') setFinanceiro((rFin.value.data as FinanceiroLinha) ?? null);
        if (rPort.status === 'fulfilled')
          setPortaria((rPort.value.data as { ingressos_validos: number }) ?? null);
        if (rResumo.status === 'fulfilled') setResumo((rResumo.value.data as ResumoLinha[]) || []);
        if (rFunil.status === 'fulfilled' && rFunil.value.data)
          setFunil(rFunil.value.data as FunilLinha);
        if (rFunilV2.status === 'fulfilled' && Array.isArray(rFunilV2.value.data)) {
          setFunilPublicos(rFunilV2.value.data as FunilPublico[]);
        } else {
          setFunilPublicos([]);
        }
        if (rSem.status === 'fulfilled' && rSem.value.data)
          setSemEquipe((rSem.value.data as unknown[]).length);

        if (rEv.status === 'fulfilled' && rEv.value.data) {
          setEventoRascunho(rEv.value.data as EventoRascunho);
        } else {
          setEventoRascunho(null);
        }

        if (rLoja.status === 'fulfilled' && rLoja.value.data) {
          const painel = rLoja.value.data as {
            estado?: string;
            estornos_pendentes?: number;
            pedidos_pagos?: number | null;
          } | null;
          if (painel) {
            setLojaEstado(painel.estado ?? null);
            setEstornosPendentes(Number(painel.estornos_pendentes) || 0);
            const pagos = painel.pedidos_pagos;
            setFichasPagas(pagos === null || pagos === undefined ? null : Number(pagos) || 0);
          }
        }

        if (rProd.status === 'fulfilled') {
          const c = (rProd.value as { count?: number | null }).count;
          setTemProdutoLoja(Number(c) > 0);
        }

        if (rRifa.status === 'fulfilled' && rRifa.value.data) {
          const linha = rRifa.value.data as {
            status: string;
            premios: { imagem_url: string | null }[] | null;
          } | null;
          if (linha?.status === 'publicada' && linha.premios) {
            setPremiosSemFoto(linha.premios.filter((p) => !p.imagem_url).length);
          }
        }

        // Só leitura: se a rpc não responder (ex.: sem acesso), mantém o valor antigo.
        if (rFinanc.status === 'fulfilled' && rFinanc.value.data) {
          const fin = rFinanc.value.data as {
            evento?: { mp_conectado?: boolean };
            resumo?: { liquido?: number | null };
            onde_esta?: { direto?: number | null };
          } | null;
          const liq = fin?.resumo?.liquido;
          setLiquidoCentavos(liq === null || liq === undefined ? null : Number(liq));
          // Split: o dinheiro cai direto na conta do evento (Mercado Pago conectado).
          setRecebeDireto(
            fin?.evento?.mp_conectado === true || Number(fin?.onde_esta?.direto) > 0,
          );
        }

        // Números de pessoas: com vaga e suplentes separados de verdade (painel_vagas).
        if (rVagas.status === 'fulfilled' && !rVagas.value.error) {
          const grupos = (rVagas.value.data as GrupoVagas[] | null) || [];
          const papelPorModalidade = new Map<number, string>();
          if (rMods.status === 'fulfilled' && rMods.value.data) {
            (rMods.value.data as { id: number; papel: string | null }[]).forEach((m) => {
              if (m.papel) papelPorModalidade.set(Number(m.id), m.papel);
            });
          }
          const zero = (): VagasResumo => ({ comVaga: 0, suplentes: 0 });
          const conta: VagasPorPapel = { equipe: zero(), campista: zero(), total: zero() };
          const alerta = { livres: 0, nomes: [] as string[] };
          grupos.forEach((g) => {
            const comVaga = Number(g.com_vaga) || 0;
            const suplentes = Number(g.suplentes) || 0;
            conta.total.comVaga += comVaga;
            conta.total.suplentes += suplentes;
            const papel = papelPorModalidade.get(Number(g.modalidade_id));
            if (papel === 'equipe' || papel === 'campista') {
              conta[papel].comVaga += comVaga;
              conta[papel].suplentes += suplentes;
            }
            if (g.alerta_vaga_livre) {
              alerta.livres += Number(g.livres) || 0;
              alerta.nomes.push(nomeDoGrupo(g));
            }
          });
          setVagasResumo(conta);
          setAlertasVagas(alerta);
        }

        if (separaEquipeCampista && rMods.status === 'fulfilled' && rInsc.status === 'fulfilled') {
          const papelPorModalidade = new Map<number, string>();
          ((rMods.value.data as { id: number; papel: string | null }[]) || []).forEach((m) => {
            if (m.papel) papelPorModalidade.set(Number(m.id), m.papel);
          });
          const conta = {
            equipe: { total: 0, pendente: 0 },
            campista: { total: 0, pendente: 0 },
          };
          ((rInsc.value.data as { modalidade_id: number; status: string }[]) || []).forEach(
            (i) => {
              // Suplente ainda não deve nada: não entra em "aguardando pagamento".
              if (i.status === 'cancelada' || i.status === 'lista_espera') return;
              const p = papelPorModalidade.get(Number(i.modalidade_id));
              if (p !== 'equipe' && p !== 'campista') return;
              conta[p].total += 1;
              if (i.status === 'pendente') conta[p].pendente += 1;
            },
          );
          setPendentesPorPapel(conta);
        }

        setCarregando(false);
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar o resumo.'));
          setCarregando(false);
        }
      }
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [eventoId, mostrarEquipes, mostrarLoja, separaEquipeCampista, status]);

  // Pessoas com vaga (nunca somando equipe + campista) e suplentes à parte.
  const pessoasComVaga = vendeIngressos
    ? Number(portaria?.ingressos_validos) || 0
    : vagasResumo
      ? vagasResumo.total.comVaga
      : Number(financeiro?.inscricoes) || 0;
  const pessoasSuplentes = vagasResumo?.total.suplentes ?? 0;
  const totalPessoas = vendeIngressos ? pessoasComVaga : pessoasComVaga + pessoasSuplentes;

  // O que falta para um rascunho ir ao ar (mesma ideia da revisão de publicar).
  const faltaRascunho = useMemo<string[]>(() => {
    if (!eventoRascunho) return [];
    const faltas: string[] = [];
    if (!eventoRascunho.imagem_desktop_url) faltas.push('arte do evento');
    if (!eventoRascunho.inicio_em) faltas.push('data de início');
    if (!eventoRascunho.local_nome && !eventoRascunho.cidade) faltas.push('local');
    if (!(pessoasComVaga > 0 || temProdutoLoja)) faltas.push('tipos de inscrição ou fichas');
    return faltas;
  }, [eventoRascunho, pessoasComVaga, temProdutoLoja]);

  const pendencias = useMemo<Pendencia[]>(() => {
    const lista: Pendencia[] = [];
    const pendentes = Number(financeiro?.com_pendencia) || 0;
    // Vaga livre com suplente esperando: só existe tela de vagas com campistas.
    if (separaEquipeCampista && alertasVagas.livres > 0 && alertasVagas.nomes.length > 0) {
      const n = alertasVagas.livres;
      const rotulo =
        alertasVagas.nomes.length === 1
          ? `${n} ${n === 1 ? 'vaga livre' : 'vagas livres'} em ${alertasVagas.nomes[0]}`
          : `${n} ${n === 1 ? 'vaga livre' : 'vagas livres'} em ${alertasVagas.nomes.length} grupos`;
      lista.push({
        chave: 'vagas-livres',
        rotulo,
        alvo: 'campistas',
        extra: { tela: 'vagas' },
        tom: 'aviso',
      });
    }
    if (pendentes > 0) {
      if (separaEquipeCampista && pendentesPorPapel) {
        if (pendentesPorPapel.equipe.pendente > 0) {
          lista.push({
            chave: 'pagamento-equipe',
            rotulo: `${pendentesPorPapel.equipe.pendente} da equipe aguardando pagamento`,
            alvo: 'equipe_acampamento',
            extra: { tela: 'pessoas', situacao: 'pendente' },
            tom: 'aviso',
          });
        }
        if (pendentesPorPapel.campista.pendente > 0) {
          lista.push({
            chave: 'pagamento-campista',
            rotulo: `${pendentesPorPapel.campista.pendente} campistas aguardando pagamento`,
            alvo: 'campistas',
            extra: { tela: 'pessoas', situacao: 'pendente' },
            tom: 'aviso',
          });
        }
      } else {
        lista.push({
          chave: 'pagamento',
          rotulo: `${pendentes} aguardando pagamento`,
          alvo: separaEquipeCampista ? 'equipe_acampamento' : 'pessoas',
          extra: separaEquipeCampista
            ? { tela: 'pessoas', situacao: 'pendente' }
            : { situacao: 'pendente' },
          tom: 'aviso',
        });
      }
    }
    if (mostrarEquipes && semEquipe > 0) {
      lista.push({
        chave: 'sem-equipe',
        rotulo: `${semEquipe} sem equipe`,
        alvo: separaEquipeCampista ? 'equipe_acampamento' : 'equipes',
        extra: separaEquipeCampista ? { tela: 'montagem' } : undefined,
        tom: 'aviso',
      });
    }
    if (
      mostrarLoja &&
      temProdutoLoja &&
      lojaEstado &&
      lojaEstado !== 'aberta' &&
      lojaEstado !== 'encerrada'
    ) {
      lista.push({
        chave: 'loja-fechada',
        rotulo: 'Vendas da loja fechadas',
        alvo: 'dia',
        extra: { tela: 'estado' },
        tom: 'aviso',
      });
    }
    if (estornosPendentes > 0) {
      lista.push({
        chave: 'estornos',
        rotulo: `${estornosPendentes} ${estornosPendentes === 1 ? 'estorno pendente' : 'estornos pendentes'}`,
        alvo: 'financeiro',
        tom: 'urgente',
      });
    }
    if (premiosSemFoto > 0) {
      lista.push({
        chave: 'premios-sem-foto',
        rotulo: `Ação entre amigos: ${premiosSemFoto} ${premiosSemFoto === 1 ? 'prêmio sem foto' : 'prêmios sem foto'}`,
        alvo: 'sorteio',
        tom: 'aviso',
      });
    }
    if (status === 'rascunho') {
      lista.push({
        chave: 'rascunho',
        rotulo: 'Rascunho: ninguém vê ainda',
        detalhe:
          faltaRascunho.length > 0
            ? `Falta: ${listaDeFaltas(faltaRascunho)}`
            : 'Tudo pronto para publicar',
        rotuloAcao: 'Revisar e publicar',
        alvo: 'inicio',
        acao: 'publicar',
        tom: 'urgente',
      });
    }
    return lista;
  }, [
    financeiro,
    semEquipe,
    mostrarEquipes,
    mostrarLoja,
    temProdutoLoja,
    lojaEstado,
    estornosPendentes,
    premiosSemFoto,
    status,
    separaEquipeCampista,
    pendentesPorPapel,
    alertasVagas,
    faltaRascunho,
  ]);

  // Resumo em seções: Equipe e Campistas nunca se somam.
  const gruposResumo = useMemo<GrupoResumo[]>(() => {
    const grupos: { chave: string; papel: 'equipe' | 'campista' | null; linhas: ResumoLinha[] }[] =
      [];
    const indice = new Map<string, number>();
    resumo.forEach((l) => {
      const chave = l.bloco.startsWith('EQUIPE · ')
        ? 'equipe'
        : l.bloco.startsWith('CAMPISTAS · ')
          ? 'campista'
          : 'geral';
      if (!indice.has(chave)) {
        indice.set(chave, grupos.length);
        grupos.push({
          chave,
          papel: chave === 'geral' ? null : (chave as 'equipe' | 'campista'),
          linhas: [],
        });
      }
      grupos[indice.get(chave)!].linhas.push(l);
    });

    return grupos.map((g) => {
      const achar = (item: string) =>
        Number(g.linhas.find((l) => l.item === item)?.quantidade ?? 0) || 0;
      const pessoas = achar('Inscrições');
      const suplentes = achar('Suplentes (fora das contas)');
      let titulo = 'Resumo';
      let subtitulo: string | null = null;
      if (g.papel === 'equipe') {
        titulo = `Equipe — ${pessoas} ${pessoas === 1 ? 'pessoa' : 'pessoas'}`;
        if (suplentes > 0) subtitulo = `${suplentes} suplentes fora das contas`;
      } else if (g.papel === 'campista') {
        titulo = `Campistas — ${pessoas} com vaga`;
        if (suplentes > 0) subtitulo = `${suplentes} suplentes fora das contas`;
      }
      return { chave: g.chave, titulo, subtitulo, blocos: agruparBlocos(g.linhas) };
    });
  }, [resumo]);

  // Um bloco só chamado "Resumo" não repete o título do cartão.
  const resumoUnico = gruposResumo.length === 1 && gruposResumo[0].titulo === 'Resumo';

  const recebido = Number(financeiro?.recebido_centavos) || 0;
  // Usa o líquido da rpc do Financeiro quando disponível; senão, cai no valor de sempre.
  const recebidoMostrado = liquidoCentavos ?? recebido;
  const aReceber = Math.max(0, Number(financeiro?.a_receber_centavos) || 0);
  const pessoasHoje = Number(funil?.pessoas_hoje) || 0;
  // Zero vendas é diferente de "tudo pago".
  const semVendas = vendeIngressos && pessoasComVaga === 0;
  const tudoPago = vendeIngressos && !semVendas && aReceber === 0;

  // Quermesse: entrada franca que vende fichas (mesmo critério do "Abrir o caixa").
  const ehQuermesse = entradaFranca && mostrarLoja;
  // Nessa tela o número de quem comprou vem dos pedidos de ficha pagos.
  const compradoresFichas = ehQuermesse ? fichasPagas : null;
  const tudoNaConta = ehQuermesse && aReceber === 0 && recebeDireto;

  const pessoasQueViram = Number(funil?.pessoas_que_viram) || 0;
  const periodoDias = Number(funil?.periodo_dias) || 30;

  // Uma linha por público. Nunca somamos os públicos (equipe e campistas vivem separados).
  // Só o campo recado é mostrado: ele já vem completo do funil_do_evento_v2.
  const linhasFunil: FunilPublico[] = (() => {
    const linhas = funilPublicos.filter((l) => l && l.publico && (l.recado ?? '').trim());
    if (linhas.length > 0) return linhas;
    // Sem o funil por público, uma única linha "Inscritos" com o que temos.
    if (pessoasQueViram > 0 || pessoasComVaga > 0 || pessoasSuplentes > 0) {
      const partes: string[] = [
        `Inscritos · ${pessoasComVaga.toLocaleString('pt-BR')} com vaga`,
      ];
      if (pessoasSuplentes > 0) {
        partes.push(
          `${pessoasSuplentes.toLocaleString('pt-BR')} ${
            pessoasSuplentes === 1 ? 'suplente' : 'suplentes'
          }`,
        );
      }
      return [
        {
          publico: 'Inscritos',
          papel: null,
          pessoas_que_viram: pessoasQueViram,
          fichas_comecadas: null,
          fichas_confirmadas: pessoasComVaga,
          fichas_pagas: vendeIngressos ? pessoasComVaga : null,
          suplentes: pessoasSuplentes,
          recado: partes.join(' · '),
        },
      ];
    }
    return [];
  })();
  // Quem viu é a mesma página para todos: basta a primeira linha do funil (nunca somar).
  const viramTotal =
    Number(funilPublicos[0]?.pessoas_que_viram) || (Number(funil?.pessoas_que_viram) || 0);

  const mostrarSemEquipe = separaEquipeCampista && mostrarEquipes;

  if (carregando) {
    return (
      <div>
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 esqueleto rounded-3xl bg-secondary-100" />
          ))}
        </div>
        <div className="mt-4 h-64 esqueleto rounded-3xl bg-secondary-100" />
      </div>
    );
  }

  if (erro) {
    return (
      <div className={`${CARD} p-10 text-center`}>
        <p className="text-[15px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600"
        >
          Tentar de novo
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tiles 2×2 */}
      <div className="grid grid-cols-2 gap-3">
        {ehQuermesse ? (
          compradoresFichas !== null && (
            <TilePainel
              icone="ri-user-line"
              tom="primary"
              valor={Number(compradoresFichas).toLocaleString('pt-BR')}
              legenda="Compradores de fichas"
            />
          )
        ) : separaEquipeCampista ? (
          <TileInscritos
            equipe={
              vagasResumo?.equipe ?? {
                comVaga: pendentesPorPapel?.equipe.total ?? 0,
                suplentes: 0,
              }
            }
            campista={
              vagasResumo?.campista ?? {
                comVaga: pendentesPorPapel?.campista.total ?? 0,
                suplentes: 0,
              }
            }
            legenda=""
            aoClicar={() => aoIrPara('equipe_acampamento', { sub: 'pessoas' })}
          />
        ) : (
          <TilePainel
            icone="ri-user-line"
            tom="primary"
            valor={Number(pessoasComVaga).toLocaleString('pt-BR')}
            legenda={rotuloPessoas}
            apoio={
              pessoasSuplentes > 0
                ? `${Number(pessoasSuplentes).toLocaleString('pt-BR')} suplentes`
                : undefined
            }
            aoClicar={() =>
              separaEquipeCampista
                ? aoIrPara('equipe_acampamento', { sub: 'pessoas' })
                : aoIrPara('pessoas')
            }
          />
        )}
        <TilePainel
          icone="ri-money-dollar-circle-line"
          tom="success"
          valor={formatarDinheiro(recebidoMostrado)}
          legenda="Recebido"
          apoio="líquido para o evento"
        />
        <TilePainel
          icone={
            ehQuermesse
              ? aReceber === 0
                ? 'ri-check-double-line'
                : 'ri-hourglass-line'
              : semVendas
                ? 'ri-ticket-2-line'
                : tudoPago
                  ? 'ri-check-double-line'
                  : 'ri-hourglass-line'
          }
          tom={
            ehQuermesse
              ? aReceber === 0
                ? 'success'
                : 'warning'
              : semVendas
                ? 'primary'
                : tudoPago
                  ? 'success'
                  : 'warning'
          }
          valor={
            ehQuermesse
              ? formatarDinheiro(aReceber)
              : semVendas
                ? 'Nenhuma venda ainda'
                : tudoPago
                  ? 'Tudo pago'
                  : formatarDinheiro(aReceber)
          }
          legenda="A receber"
          apoio={tudoNaConta ? 'tudo já caiu na sua conta' : undefined}
          destaqueTudoPago={!ehQuermesse && tudoPago}
          valorBase={!ehQuermesse && semVendas ? 'text-[15px] leading-tight' : undefined}
          valorQuebra={!ehQuermesse && semVendas}
        />
        {mostrarSemEquipe ? (
          <TilePainel
            icone="ri-team-line"
            tom={semEquipe > 0 ? 'warning' : 'success'}
            valor={Number(semEquipe).toLocaleString('pt-BR')}
            legenda="Sem equipe"
            aoClicar={() => aoIrPara('equipe_acampamento', { sub: 'montagem' })}
          />
        ) : (
          <TilePainel
            icone="ri-eye-line"
            tom="primary"
            valor={Number(pessoasHoje).toLocaleString('pt-BR')}
            legenda="Viram hoje"
          />
        )}
      </div>

      {/* Seu agente: a primeira linha do resumo do dia */}
      {(estadoAgente === 'carregando' || (estadoAgente === 'pronto' && primeiraLinhaAgente)) && (
        <button
          type="button"
          onClick={() => navigate(`/gerenciar/${eventoId}/assistente`)}
          className="w-full cursor-pointer rounded-[16px] bg-white p-4 text-left shadow-[0_6px_18px_rgba(20,25,60,.06)] transition-colors hover:bg-primary-50/40"
        >
          <span className="flex items-center justify-between gap-3">
            <span className="min-w-0 text-[12px] font-semibold tracking-wide text-foreground-500">
              SEU AGENTE · HOJE
            </span>
            <span className="shrink-0 whitespace-nowrap text-[14px] font-semibold text-primary-600">
              Ver tudo
            </span>
          </span>
          {estadoAgente === 'carregando' ? (
            <span className="mt-2 block space-y-1.5">
              <span className="block h-3.5 w-full esqueleto rounded-full bg-background-200/60" />
              <span className="block h-3.5 w-2/3 esqueleto rounded-full bg-background-200/60" />
            </span>
          ) : (
            <span
              className="mt-1 line-clamp-2 overflow-hidden text-[15px] leading-relaxed text-foreground-800"
              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
            >
              {primeiraLinhaAgente}
            </span>
          )}
        </button>
      )}

      {/* Atalhos: mudam pelo tipo de evento */}
      <AtalhosInicio
        acampamento={separaEquipeCampista}
        mostrarLoja={mostrarLoja}
        vendeIngressos={vendeIngressos}
        mostrarEquipes={mostrarEquipes}
        rotuloPessoas={rotuloPessoas}
        aoIrPara={aoIrPara}
        aoAbrirCaixa={() => navigate('/caixa')}
      />

      {/* Quermesse: fala de fichas, nunca de inscritos/ingressos */}
      {ehQuermesse
        ? compradoresFichas === 0 && (
            <div className={`${CARD} flex flex-wrap items-center gap-3`}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
                <i className="ri-store-2-line text-lg" aria-hidden />
              </span>
              <p className="min-w-0 flex-1 text-[15px] font-semibold leading-snug text-foreground-900">
                Ainda sem fichas vendidas pelo site.
              </p>
              <button
                type="button"
                onClick={() => aoIrPara('inicio', { divulgar: '1' })}
                className="aperta flex min-h-[44px] shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 px-4 text-[13px] font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Ver o link de divulgação
              </button>
            </div>
          )
        : (vendeIngressos ? semVendas : totalPessoas === 0) && (
            <DicaPrimeiraVez
              id="painel.inicio.vazio"
              eventoId={eventoId}
              titulo={
                vendeIngressos
                  ? 'Seu evento ainda não vendeu ingressos'
                  : 'Seu evento ainda não tem inscritos'
              }
              texto={
                vendeIngressos
                  ? `Quem comprar aparece em ${rotuloPessoas}.`
                  : `Quem se inscrever aparece em ${
                      separaEquipeCampista ? 'Equipe e Campistas' : rotuloPessoas
                    }.`
              }
            />
          )}

      {/* Mandar aviso no celular */}
      <MandarAviso
        eventoId={eventoId}
        slug={slug}
        texto={
          ehQuermesse
            ? 'Envie um recado curto para a equipe do dia ou para quem comprou fichas.'
            : separaEquipeCampista
              ? 'Envie um recado curto para campistas, equipe ou vendedores da ação.'
              : undefined
        }
      />

      {/* Precisa de você */}
      {pendencias.length === 0 ? (
        <div className={`${CARD} flex items-center gap-3`}>
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-success-100 text-success-600">
            <i className="ri-check-line text-lg" />
          </span>
          <p className="text-[15px] font-medium text-success-800">Tudo em dia.</p>
        </div>
      ) : (
        <div className={CARD}>
          <h2 className="px-1 pt-1 text-[17px] font-bold text-foreground-950">Precisa de você</h2>
          <div className="mt-2">
            <ListaEntrada itens={pendencias} chave={(p) => p.chave}>
              {(p) => (
                <button
                  type="button"
                  onClick={() =>
                    p.acao === 'publicar' ? aoPublicar() : aoIrPara(p.alvo, p.extra)
                  }
                  className="flex min-h-[56px] w-full cursor-pointer items-center justify-between gap-3 rounded-2xl px-1 py-2 text-left transition-colors hover:bg-background-50"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <PulsoDuas
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                        p.tom === 'urgente'
                          ? 'bg-danger-100 text-danger-600'
                          : 'bg-warning-100 text-warning-700'
                      }`}
                    >
                      <i
                        className={
                          p.tom === 'urgente' ? 'ri-error-warning-line' : 'ri-alert-line'
                        }
                      />
                    </PulsoDuas>
                    <span className="min-w-0">
                      <span className="block text-[15px] font-semibold text-foreground-900">
                        {p.rotulo}
                      </span>
                      <span className="block text-[13px] text-foreground-500">
                        {p.detalhe ?? (p.tom === 'urgente' ? 'resolva agora' : 'toque para ver')}
                      </span>
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    {p.rotuloAcao ? (
                      <span className="whitespace-nowrap rounded-full bg-primary-50 px-3 py-1.5 text-[12px] font-semibold text-primary-700">
                        {p.rotuloAcao}
                      </span>
                    ) : (
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center text-foreground-300">
                        <i className="ri-arrow-right-s-line text-2xl" />
                      </span>
                    )}
                  </span>
                </button>
              )}
            </ListaEntrada>
          </div>
        </div>
      )}

      {/* Resumo: recolhido, só abre com "Ver resumo completo" */}
      {gruposResumo.length > 0 && (
        <div className={CARD}>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-secondary-100 text-secondary-900">
              <i className="ri-bar-chart-2-line text-lg" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-[15px] font-bold text-foreground-950">Resumo</h2>
              {!resumoUnico && (
                <p className="mt-0.5 truncate text-[12.5px] text-foreground-600">
                  {gruposResumo.map((g) => g.titulo).join(' · ')}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setResumoAberto((v) => !v)}
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-3 py-2 text-[12px] font-semibold text-primary-700 transition-colors hover:bg-background-100"
            >
              {resumoAberto ? 'Recolher' : 'Ver resumo completo'}
            </button>
          </div>

          {resumoAberto && (
            <div className="mt-4 space-y-4">
              {gruposResumo.map((g) => (
                <div key={g.chave}>
                  {!(resumoUnico && g.titulo === 'Resumo') && (
                    <div className="px-1">
                      <h3 className="text-[15px] font-bold text-foreground-950">{g.titulo}</h3>
                      {g.subtitulo && (
                        <p className="mt-0.5 text-[12.5px] text-foreground-600">{g.subtitulo}</p>
                      )}
                    </div>
                  )}
                  <ResumoColapsavel blocos={g.blocos} todos />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Audiência por público (funil_do_evento_v2): uma linha por público, sem somar */}
      {linhasFunil.length > 0 && (
        <div className={CARD}>
          <p className="text-[14px] leading-relaxed text-foreground-700">
            <strong className="font-semibold text-foreground-950">
              {viramTotal.toLocaleString('pt-BR')}
            </strong>{' '}
            {viramTotal === 1 ? 'viu' : 'viram'} a página nos últimos {periodoDias} dias
            {compradoresFichas !== null && (
              <>
                {' · '}
                <strong className="font-semibold text-foreground-950">
                  {Number(compradoresFichas).toLocaleString('pt-BR')}
                </strong>{' '}
                {compradoresFichas === 1 ? 'comprou fichas' : 'compraram fichas'}
              </>
            )}
          </p>

          <div className="mt-3 space-y-2.5">
            {linhasFunil.map((l) => {
              const recado = (l.recado ?? '').trim();
              if (!recado) return null;
              return (
                <p
                  key={`${l.publico}-${l.papel ?? 'x'}`}
                  className="text-[14px] leading-relaxed text-foreground-600"
                >
                  {recado}
                </p>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ResumoColapsavel({ blocos, todos = false }: { blocos: BlocoResumo[]; todos?: boolean }) {
  const [aberto, setAberto] = useState(false);
  const visiveis = todos || aberto ? blocos : blocos.slice(0, 2);

  return (
    <div className="mt-2 rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {visiveis.map((b) => (
          <div key={b.bloco} className="rounded-2xl bg-background-50 p-4">
            <h3 className="text-[14px] font-semibold text-foreground-950">{b.titulo}</h3>
            <dl className="mt-2 space-y-1.5">
              {b.linhas.map((l, i) => (
                <div key={i} className="flex items-baseline justify-between gap-3">
                  <dt className="text-[14px] text-foreground-600">{l.item}</dt>
                  <dd className="shrink-0 text-[14px] font-bold text-foreground-950">
                    {l.quantidade === null || l.quantidade === undefined
                      ? '—'
                      : Number(l.quantidade).toLocaleString('pt-BR')}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
      {!todos && blocos.length > 2 && (
        <button
          type="button"
          onClick={() => setAberto((a) => !a)}
          className="mt-3 w-full cursor-pointer whitespace-nowrap rounded-2xl bg-background-100 py-2 text-[13px] font-semibold text-primary-600 transition-colors hover:bg-background-200"
        >
          {aberto ? 'ver menos' : `ver mais (${blocos.length - 2})`}
        </button>
      )}
    </div>
  );
}